/* eslint-disable no-param-reassign */
import qs from 'qs'
import { useUserStore, useLanguageStore } from '@/store'
import { platform } from '@/utils/platform'

export type CustomRequestOptions = UniApp.RequestOptions & {
  query?: Record<string, any>
  /** 出错时是否隐藏错误提示 */
  hideErrorToast?: boolean
} & IUniUploadFileOptions // 添加uni.uploadFile参数类型

// 请求基准地址
const proxyMap = {
  main: import.meta.env.VITE_SERVER_BASEURL,
  idass: import.meta.env.VITE_IDASS_BASEURL,
  noApp: import.meta.env.VITE_NOAPPSERVER_BASEURL,
  upload: import.meta.env.VITE_UPLOAD_BASEURL,
}

/**
 * 路径拼接规则（不含 baseUrl，H5 端用相对路径）
 *
 * /app/sso/...   -> /app/sso/...           （保持原样）
 * /app/infra/... -> /app/infra/...         （保持原样）
 * /uaa/...       -> /uaa/...              （保持原样）
 * /common/...    -> /app/expedia/common/...
 * /query/...     -> /app/expedia/common/query/...
 * /command/...   -> /app/expedia/common/command/...
 * (其他)         -> /app/expedia/common + path
 */
function resolvePath(url: string): string {
  if (url.startsWith('/app/sso') || url.startsWith('/app/infra') || url.startsWith('/uaa')) {
    return url
  }
  if (url.startsWith('/common')) {
    return `/app/expedia${url}`
  }
  // /query/xxx, /command/xxx, 其他 -> /app/expedia/common/xxx
  return `/app/expedia/common${url}`
}

// 拦截器配置
const httpInterceptor = {
  // 拦截前触发
  invoke(options: CustomRequestOptions) {
    // 接口请求支持通过 query 参数配置 queryString
    if (options.query) {
      const queryStr = qs.stringify(options.query)
      if (options.url.includes('?')) {
        options.url += `&${queryStr}`
      } else {
        options.url += `?${queryStr}`
      }
    }

    // 非 http 开头需拼接地址
    if (!options.url.startsWith('http')) {
      // 先统一做路径转换
      options.url = resolvePath(options.url)
      console.log('先统一做路径转换', options.url)

      // #ifdef H5
      // H5 端使用相对路径，由 vite proxy（dev）或 nginx（prod）转发，不会跨域
      // options.url 保持相对路径即可，如 /app/expedia/common/query/hotel/page
      // #endif

      // #ifndef H5
      // 小程序/App 端：拼接完整 baseUrl
      options.url = `${proxyMap.main}${options.url}`
      console.log('小程序/App 端：拼接完整 baseUrl', options.url)

      // #endif
    }

    // 1. 请求超时
    options.timeout = 60000 // 60s
    // 2. 添加请求头标识
    options.header = {
      platform,
      ...options.header,
    }
    // 3. 添加 token 请求头标识
    const userStore = useUserStore()
    const { token } = (userStore?.userInfo as unknown as IUserInfo) || { token: null }

    if (token) {
      options.header.Authorization = `Bearer ${token}`
    }

    // 4. 添加 accept-language 请求头
    const languageStore = useLanguageStore()
    options.header['accept-language'] = languageStore?.currentLanguage?.languageCode || 'en-US'

    // #ifdef WEB
    options.header['X-Hixtrip-Channel'] = 'OVERSEAS'
    // #endif

    // #ifdef MP-WEIXIN
    options.header['X-Hixtrip-Channel'] = 'WeChat'
    // #endif
  },
}

export const requestInterceptor = {
  install() {
    uni.addInterceptor('request', httpInterceptor)
    uni.addInterceptor('uploadFile', httpInterceptor)
  },
}
